/* ==========================================================================
   Admin Session Expired Modal
   Task F26: Displays a modal when the admin user's session has expired,
   prompting them to log in again.
   ========================================================================== */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { LogIn, AlertCircle } from 'lucide-react'
import { useAdminAuthContext } from '@/context/AdminAuthContext'
import { usePortalSlug } from '@/context/PortalContext'
import {
  adminSessionExpiredEvent,
  ADMIN_SESSION_EXPIRED_EVENT,
  type SessionExpiredDetail,
} from '@/utils/sessionEvents'

/* --------------------------------------------------------------------------
   Admin Session Expired Modal Component
   -------------------------------------------------------------------------- */

/**
 * The backend's generic credential-failure detail — returned for a wrong password,
 * an unknown email, and an account with no password set. We keep our own copy for
 * that case because it adds the "try again" nudge; every other message is shown
 * as-is, since each tells the user something this generic string would hide.
 */
const GENERIC_CREDENTIAL_ERROR = 'Invalid email or password'

/**
 * Modal that displays when an admin session expires.
 * Allows admin users to re-login without losing their current page context.
 *
 * This component should be placed within AdminAuthProvider and will
 * automatically show when an ADMIN_SESSION_EXPIRED_EVENT is dispatched.
 *
 * @example
 * ```tsx
 * function AdminLayout() {
 *   return (
 *     <AdminAuthProvider>
 *       <AdminSessionExpiredModal />
 *       <AdminContent />
 *     </AdminAuthProvider>
 *   )
 * }
 * ```
 */
export function AdminSessionExpiredModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [terminal, setTerminal] = useState(false)

  // Mirrors `isOpen` for the event listener, which is registered once with []
  // deps and would otherwise close over a stale value.
  const isOpenRef = useRef(false)

  const { login, logout, user } = useAdminAuthContext()
  const slug = usePortalSlug()
  const navigate = useNavigate()
  const location = useLocation()

  // Pre-fill email if we have the user's info
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user?.email])

  // Listen for admin session expired events
  useEffect(() => {
    const handleSessionExpired = (e: Event) => {
      // Already open? Ignore. The first 401 cleared the stored token, so the page
      // behind this modal is still mounted and unauthenticated — any request it
      // makes now 401s with a detail we deliberately don't surface. Acting on that
      // would blank a specific message (e.g. "your account has been disabled"),
      // discard the error on screen, and wipe whatever the user has typed.
      if (isOpenRef.current) return

      const detail = (e as CustomEvent<SessionExpiredDetail | undefined>).detail
      setMessage(detail?.message ?? null)
      setTerminal(detail?.terminal ?? false)
      isOpenRef.current = true
      setIsOpen(true)
      setError(null)
      setPassword('')
    }

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired)

    return () => {
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired)
    }
  }, [])

  /**
   * Leave the modal for a public admin auth page.
   *
   * Closing is not optional: this modal is mounted by AdminLayoutWrapper next to
   * the <Outlet />, so it survives navigation between admin routes and would sit
   * on top of the destination. And the 401 interceptor only cleared the *stored*
   * token — the in-memory user is untouched, so `isAuthenticated` is still true
   * and AdminLogin's "already authenticated" effect would bounce us straight back
   * to the page that 401'd. Drop the session before navigating.
   */
  const leaveToAuthPage = useCallback(
    async (path: string) => {
      isOpenRef.current = false
      setIsOpen(false)
      await logout()
      navigate(path, { replace: true })
    },
    [navigate, logout]
  )

  const handleGoToLogin = useCallback(() => {
    // AdminLogin reads ?redirect=, not location.state — pass it the way it expects.
    const loginPath = slug ? `/rx/${slug}/admin/login` : '/admin/login'
    const redirect = encodeURIComponent(location.pathname + location.search)
    return leaveToAuthPage(`${loginPath}?redirect=${redirect}`)
  }, [slug, leaveToAuthPage, location.pathname, location.search])

  const handleForgotPassword = useCallback(
    () => leaveToAuthPage(`/rx/${slug}/admin/forgot-password`),
    [slug, leaveToAuthPage]
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setLoading(true)

      try {
        const result = await login(email, password, true)
        if (result.success) {
          isOpenRef.current = false
          setIsOpen(false)
          setPassword('')
          // Stay on current page - the admin's work is preserved
        } else if (!result.error) {
          // PORTAL-187: password accepted, authenticator code still needed. The
          // code step lives on the login page, which keeps the pending challenge.
          await handleGoToLogin()
        } else {
          // Show the real reason — disabled account, rate limit, invitation not yet
          // accepted — instead of blaming the password for all of them. A genuine
          // credential failure keeps our friendlier copy.
          const reason = result.error
          setError(
            reason === GENERIC_CREDENTIAL_ERROR
              ? 'Invalid email or password. Please try again.'
              : reason
          )
        }
      } catch {
        setError('An error occurred. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [email, password, login, handleGoToLogin]
  )

  // Don't render if modal shouldn't be open
  if (!isOpen) return null

  return (
    <Modal
      open={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      title={terminal ? 'Account Disabled' : 'Admin Session Expired'}
      size="sm"
      showCloseButton={false}
    >
      <div className="space-y-4">
        {/* Icon */}
        <div className="mx-auto w-12 h-12 bg-[var(--color-warning-light)] rounded-full flex items-center justify-center">
          <AlertCircle
            className="w-6 h-6 text-[var(--color-warning)]"
            aria-hidden="true"
          />
        </div>

        {/* Message */}
        <p className="text-center text-[var(--color-charcoal-light)]">
          {message ?? 'Your admin session has expired. Please log in again to continue.'}
        </p>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {error}
          </Alert>
        )}

        {/* Terminal state (account disabled): no password can succeed until an
            admin re-enables the account, so offering a login form would invite a
            guessing spiral that ends in the login rate limit. Forgot-password is
            hidden too — it only issues tokens to ACTIVE accounts, so it would
            promise an email that never arrives. One honest way out. */}
        {terminal ? (
          <Button
            type="button"
            variant="primary"
            onClick={handleGoToLogin}
            className="w-full"
          >
            Go to Login Page
          </Button>
        ) : (
        /* Login Form */
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Email" htmlFor="admin-session-email">
            <Input
              id="admin-session-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loading}
            />
          </FormField>
          <FormField label="Password" htmlFor="admin-session-password">
            <Input
              id="admin-session-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </FormField>

          {/* Recovery, for the admin who cannot remember the password this modal
              is asking for. A button rather than a Link: leaving has to close the
              modal and clear the session first (see leaveToAuthPage). Needs the
              slug — there is no slug-less forgot-password route. */}
          {slug && (
            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="rounded text-sm text-deep-purple hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender disabled:opacity-50"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              className="w-full"
            >
              <LogIn className="w-4 h-4 mr-2" aria-hidden="true" />
              Log In
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleGoToLogin}
              disabled={loading}
              className="w-full"
            >
              Go to Login Page
            </Button>
          </div>
        </form>
        )}
      </div>
    </Modal>
  )
}

/**
 * Utility function to trigger the admin session expired modal.
 * Can be called from anywhere in the app (e.g., API interceptors).
 */
export function triggerAdminSessionExpired(): void {
  adminSessionExpiredEvent.dispatch()
}

export default AdminSessionExpiredModal
